import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { useMentionSearch, MentionSuggestion } from '@/hooks/useMentionSearch';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface MentionAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  minHeight?: string;
}

const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder = "What's on your mind?",
  className,
  disabled = false,
  minHeight = '80px',
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  
  const { suggestions, isLoading, searchAthletes, clearSuggestions } = useMentionSearch();

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const detectMention = useCallback((text: string, cursorPos: number) => {
    // Look backwards from cursor to find @
    let startIndex = cursorPos - 1;
    while (startIndex >= 0 && text[startIndex] !== '@' && text[startIndex] !== ' ' && text[startIndex] !== '\n') {
      startIndex--;
    }

    if (startIndex >= 0 && text[startIndex] === '@') {
      // Check if @ is at start or preceded by whitespace
      if (startIndex === 0 || /\s/.test(text[startIndex - 1])) {
        const query = text.slice(startIndex + 1, cursorPos);
        return { startIndex, query };
      }
    }
    return null;
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    onChange(newValue);

    const mention = detectMention(newValue, cursorPos);
    if (mention) {
      setMentionStartIndex(mention.startIndex);
      setMentionQuery(mention.query);
      setShowSuggestions(true);
      setSelectedIndex(0);
      searchAthletes(mention.query);
    } else {
      setShowSuggestions(false);
      setMentionQuery('');
      clearSuggestions();
    }
  };

  const insertMention = (suggestion: MentionSuggestion) => {
    if (mentionStartIndex === -1) return;

    const beforeMention = value.slice(0, mentionStartIndex);
    const afterMention = value.slice(mentionStartIndex + mentionQuery.length + 1);
    const mentionText = `@${suggestion.username || suggestion.name.replace(/\s/g, '')} `;
    
    const newValue = beforeMention + mentionText + afterMention;
    onChange(newValue);
    
    setShowSuggestions(false);
    setMentionQuery('');
    clearSuggestions();

    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = beforeMention.length + mentionText.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyDownInternal = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(suggestions[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        clearSuggestions();
        return;
      }
    }
    onKeyDown?.(e);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDownInternal}
        className={cn('resize-none', className)}
        style={{ minHeight }}
        disabled={disabled}
      />
      
      {showSuggestions && (suggestions.length > 0 || isLoading) && (
        <div
          ref={suggestionsRef}
          className="absolute left-0 right-0 top-full mt-1 bg-background border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors',
                  index === selectedIndex ? 'bg-accent' : 'hover:bg-accent/50'
                )}
                onClick={() => insertMention(suggestion)}
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={suggestion.avatar_url || undefined} alt={suggestion.name} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {getInitials(suggestion.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{suggestion.name}</p>
                  {suggestion.username && (
                    <p className="text-xs text-muted-foreground">@{suggestion.username}</p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MentionAutocomplete;
